import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  FilterBar,
  Pagination,
  Pill,
  SelectField,
  fmtBRL,
  type Column,
} from "@atlas/ui/web";
import type { BeneficioDaConsignataria, OcupacaoMargem, SituacaoBeneficio } from "@atlas/sdk";
import { atlas } from "../../../lib/sdk";
import { mensagemDeErro } from "../../../lib/erro-validacao";
import { useFiltros } from "../../../lib/use-filtros";
import { useDebouncedValue } from "../../../lib/use-debounce";
import { OCUPA_MARGEM, OPCOES_SITUACAO, ROTULO_COMPORTAMENTO, situacaoDoBeneficio } from "../../../lib/beneficios";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../../lib/banco-perms";
import { IncluirBeneficioModal } from "./incluir-modal";

// OS BENEFÍCIOS DA CONSIGNATÁRIA (#316, epic #312).
//
// Todos os descontos de benefício dela — plano de saúde, odontológico, seguro,
// mensalidade —, nos órgãos em que tem rubrica. A coluna que a decisão de 14/10
// pôs no centro é "Ocupa margem": a resposta da regra do órgão, a mesma que o
// motor de margem usa. Filtros, página e totais vêm da API (recorte no servidor).

const POR_PAGINA = 25;
const reais = (centavos: number) => fmtBRL(centavos / 100);

const OPCOES_OCUPA: { value: OcupacaoMargem | ""; label: string }[] = [
  { value: "", label: "Todos" },
  ...(Object.keys(OCUPA_MARGEM) as OcupacaoMargem[]).map((o) => ({ value: o, label: OCUPA_MARGEM[o].rotulo })),
];

function Pendencia({ b }: { b: BeneficioDaConsignataria }) {
  const itens: { texto: string; grave: boolean }[] = [];
  if (b.pedidoCancelamento) {
    itens.push({
      texto: b.pedidoCancelamento.vencido ? "Cancelamento pedido — prazo vencido" : "Cancelamento pedido",
      grave: b.pedidoCancelamento.vencido,
    });
  }
  if (b.pedidoAlteracao) {
    itens.push({
      texto: b.pedidoAlteracao.vencido ? "Alteração pedida — prazo vencido" : "Alteração pedida",
      grave: b.pedidoAlteracao.vencido,
    });
  }
  if (b.autorizacao && b.situacao !== "aguardando_autorizacao") {
    itens.push({ texto: "Aumento aguardando o servidor", grave: false });
  }
  if (itens.length === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
  return (
    <div style={{ display: "grid", gap: 2 }}>
      {itens.map((i) => (
        <span key={i.texto} style={{ color: i.grave ? "var(--danger-500)" : "var(--text)", fontWeight: i.grave ? 600 : 400 }}>
          {i.texto}
        </span>
      ))}
    </div>
  );
}

export function BancoBeneficios() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // A Visão do mês abre esta lista já no órgão clicado.
  const [unidadeId, setUnidadeId] = useState(sp.get("unidadeId") ?? "");
  const [rubrica, setRubrica] = useState("");
  const [credorFinalId, setCredorFinalId] = useState("");
  const [situacao, setSituacao] = useState<SituacaoBeneficio | "">("");
  const [ocupaMargem, setOcupaMargem] = useState<OcupacaoMargem | "">("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const buscaDebounced = useDebouncedValue(busca);
  const [incluindo, setIncluindo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // Incluir aparece só para quem opera benefícios (#320); a API confere de novo.
  const podeOperar = bancoPermissaoCobre(readBancoPermissoesDaSessao(), "beneficios-operar");

  const { filtrar, propsFilterBar } = useFiltros(
    [
      { nome: "orgao", ligado: unidadeId !== "", limpar: () => setUnidadeId("") },
      { nome: "rubrica", ligado: rubrica !== "", limpar: () => setRubrica("") },
      { nome: "credorFinal", ligado: credorFinalId !== "", limpar: () => setCredorFinalId("") },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
      { nome: "ocupaMargem", ligado: ocupaMargem !== "", limpar: () => setOcupaMargem("") },
      { nome: "q", ligado: busca !== "", limpar: () => setBusca("") },
    ],
    setPagina,
  );

  const q = useQuery({
    queryKey: ["banco", "beneficios", unidadeId, rubrica, credorFinalId, situacao, ocupaMargem, buscaDebounced, pagina],
    queryFn: () =>
      atlas.banco.beneficios.listar({
        unidadeId: unidadeId ? Number(unidadeId) : undefined,
        rubrica: rubrica || undefined,
        credorFinalId: credorFinalId ? Number(credorFinalId) : undefined,
        situacao: situacao || undefined,
        ocupaMargem: ocupaMargem || undefined,
        q: buscaDebounced.trim() || undefined,
        limit: POR_PAGINA,
        offset: (pagina - 1) * POR_PAGINA,
      }),
    placeholderData: keepPreviousData,
  });
  const facetas = q.data?.facetas;

  const colunas: Column<BeneficioDaConsignataria>[] = [
    { key: "orgao", header: "Órgão/entidade", wrap: true, render: (b) => b.orgao },
    {
      key: "servidor",
      header: "Servidor",
      wrap: true,
      render: (b) => (
        <div style={{ display: "grid" }}>
          <span>{b.nome ?? "—"}</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{b.cpfMasked}</span>
        </div>
      ),
    },
    { key: "matricula", header: "Matrícula", mono: true, render: (b) => `${b.matricula} · FF ${b.folha}` },
    {
      key: "beneficio",
      header: "Benefício",
      wrap: true,
      render: (b) => (
        <div style={{ display: "grid" }}>
          <span>{b.rotulo}</span>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>rubrica {b.rubrica}</span>
        </div>
      ),
    },
    { key: "credorFinal", header: "Credor final", wrap: true, render: (b) => b.credorFinal?.nome ?? "—" },
    { key: "valor", header: "Valor", align: "right", render: (b) => reais(b.valorCentavos) },
    { key: "comportamento", header: "Desconto", render: (b) => ROTULO_COMPORTAMENTO[b.comportamento] ?? b.comportamento },
    {
      key: "situacao",
      header: "Situação",
      render: (b) => {
        const s = situacaoDoBeneficio(b.situacao, b.competenciaFim);
        return <Pill variant={s.variante}>{s.rotulo}</Pill>;
      },
    },
    {
      key: "ocupaMargem",
      header: "Ocupa margem",
      render: (b) => {
        const o = OCUPA_MARGEM[b.ocupaMargem];
        return (
          <span title={o.explicacao}>
            <Pill variant={o.variante}>{o.rotulo}</Pill>
          </span>
        );
      },
    },
    { key: "pendencia", header: "Pendência", wrap: true, render: (b) => <Pendencia b={b} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Benefícios</h1>
          <div style={{ color: "var(--text-muted)" }}>
            {q.data
              ? `${q.data.totais.quantidade} benefício${q.data.totais.quantidade === 1 ? "" : "s"} · ${reais(q.data.totais.valorCentavos)} por mês`
              : "Os descontos de benefício nos órgãos/entidades em que você tem rubrica."}
          </div>
        </div>
        {podeOperar ? (
          <div style={{ marginLeft: "auto" }}>
            <Button onClick={() => setIncluindo(true)}>Incluir benefício</Button>
          </div>
        ) : null}
      </header>

      {aviso ? (
        <Card>
          <p role="status" style={{ margin: 0 }}>{aviso}</p>
        </Card>
      ) : null}
      {incluindo ? <IncluirBeneficioModal onFechar={() => setIncluindo(false)} onFeito={setAviso} /> : null}

      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por matrícula ou nome do servidor"
        {...propsFilterBar}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          <SelectField
            label="Órgão/entidade"
            value={unidadeId}
            onChange={(e) => filtrar(setUnidadeId)(e.target.value)}
            options={[{ value: "", label: "Todos" }, ...(facetas?.orgaos ?? []).map((o) => ({ value: String(o.id), label: o.nome }))]}
          />
          <SelectField
            label="Rubrica"
            value={rubrica}
            onChange={(e) => filtrar(setRubrica)(e.target.value)}
            options={[{ value: "", label: "Todas" }, ...(facetas?.rubricas ?? []).map((r) => ({ value: r.codigo, label: `${r.codigo} — ${r.rotulo}` }))]}
          />
          <SelectField
            label="Credor final"
            value={credorFinalId}
            onChange={(e) => filtrar(setCredorFinalId)(e.target.value)}
            options={[{ value: "", label: "Todos" }, ...(facetas?.credoresFinais ?? []).map((c) => ({ value: String(c.id), label: c.nome }))]}
          />
          <SelectField
            label="Situação"
            value={situacao}
            onChange={(e) => filtrar(setSituacao)(e.target.value as SituacaoBeneficio | "")}
            options={OPCOES_SITUACAO}
          />
          <SelectField
            label="Ocupa margem"
            value={ocupaMargem}
            onChange={(e) => filtrar(setOcupaMargem)(e.target.value as OcupacaoMargem | "")}
            options={OPCOES_OCUPA}
          />
        </div>
      </FilterBar>

      <Card>
        {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar os benefícios.")}</p>}
        <DataTable
          caption="Benefícios"
          columns={colunas}
          rows={q.data?.itens ?? []}
          rowKey={(b) => b.id}
          loading={q.isLoading}
          onRowClick={(b) => nav(`/banco/beneficios/${encodeURIComponent(b.id)}`)}
          emptyState={
            <EstadoVazio
              titulo={propsFilterBar.resetActive ? "Nenhum benefício com estes filtros" : "Nenhum benefício ainda"}
              orientacao={
                propsFilterBar.resetActive
                  ? undefined
                  : "Os benefícios aparecem quando a averbadora vincula uma rubrica de um órgão/entidade a você."
              }
            />
          }
        />
        <Pagination
          total={q.data?.total ?? 0}
          pagina={pagina}
          porPagina={POR_PAGINA}
          onPagina={setPagina}
          onPorPagina={() => undefined}
          rotulo="benefícios"
        />
      </Card>
    </div>
  );
}
